// usePlanBrain — React hook that fetches the aggregated Brain data
// payload and runs runPlanBrain() over the supplied plan list.
//
// Stays inside the agent quote screen — no global state. Re-runs when
// the plan ids, medications, providers, or condition profile change.
// Exposes a loading flag and the full PlanBrainResult.
//
// ─── Brain transplant adapter (agent v3 → consumer brain) ────────────
//
// The brain library was replaced with the consumer's source-of-truth
// version. Its inputs/outputs use different names + shapes than the
// agent app expects. To avoid touching the entire agent UI, this file:
//
//   1. Loads the same /api/plan-brain-data payload as before (the
//      agent's PlanBrainData shape — preserved as PlanBrainData
//      below for lookupDrugCost in QuoteDeliveryV4).
//   2. adaptToBrainInputs() converts that payload + the agent's
//      Plan / Client / Medication / Provider records into BrainInputs
//      (the new consumer shape: PmPlanRow[], Maps keyed by
//      contract-plan-segment, etc.).
//   3. runPlanBrain returns BrainOutput; adaptBrainOutput() massages
//      it back into a "compat" PlanBrainResult that mirrors the
//      pre-transplant shape the agent UI was written against.
//
// The compat types are exported below (ScoredPlan, PlanBrainResult,
// WeightProfile, RibbonKey, ConditionKey, Population, PlanBrainData)
// so the 4 dependent caller files don't need their own redefinitions.

import { useEffect, useMemo, useState } from 'react';
import type { Plan } from '@/types/plans';
import type { Client, Medication, Provider } from '@/types/session';
import { runPlanBrain } from '@/lib/plan-brain';
import { fetchGrossMonthlyPrices, monthlyQuantity } from '@/lib/drugGrossPrice';
import { checkProviderNetwork } from '@/lib/library-client';
import type {
  BrainInputs,
  BrainOutput,
  BrainScoredPlan,
  BrainWeights,
  RankPopulation,
  RibbonType,
  UserProfile,
} from '@/lib/plan-brain-types';
import type {
  PmPlanRow,
  PlanBenefitRow,
  FormularyCoverage,
  CsnpCondition,
} from '@/lib/brain-foreign-types';
import type { DetectedConditionKey } from '@/lib/condition-detector';
import { BROKER_IMPLICATIONS } from '@/lib/condition-detector';
import { ARCHETYPE_RULES } from '@/lib/broker-playbook';
import type { DualEligibleAdjustment } from '@/lib/dual-eligible';
import { normalizeLisTier } from '@/lib/dual-eligible';
import type {
  AnnualCostEstimate,
  AnnualUtilization,
} from '@/lib/utilization-model';
import { planYearOverride } from '../../api/library/planYearOverride';

// ─── Compat type re-exports ──────────────────────────────────────────
// These mirror the pre-transplant agent shape so callers (QuoteDeliveryV4,
// useAgentBaseRecommend, quotePdf, AgentV3App) keep their existing
// field-access patterns.

export type RibbonKey = RibbonType;
export type WeightProfile = BrainWeights;
export type Population = 'mapd' | 'csnp' | 'dsnp';
export type ConditionKey = DetectedConditionKey;

// Subset of agent ConditionKey + DetectedConditionKey overlap used by
// the agent app's CONDITION_LABEL map. (Old type was named `Condition`
// — kept under the same name here so the existing QuoteDeliveryV4
// import resolves.) Identical to DetectedConditionKey today.

// PlanBrainData — agent /api/plan-brain-data payload. Lookup helpers
// (lookupDrugCost in QuoteDeliveryV4) read this verbatim, so its shape
// can't shift.
export interface BenefitRow {
  benefit_type: string;
  tier_id: string | null;
  copay: number | null;
  coinsurance: number | null;
  description: string | null;
  source: string;
  // Highest-ranked LOSER of /api/plan-brain-data's source-priority
  // dedup — populated when a lower-priority source disagreed with
  // the winner. Carried through PlanBenefitRow so selectCostShare()
  // in src/lib/dual-eligible.ts can pick the value appropriate to
  // the beneficiary's MSP population.
  alt_copay?: number | null;
  alt_coinsurance?: number | null;
  alt_source?: string | null;
}
export interface DrugCostCacheRow {
  plan_id: string;
  segment_id: string;
  ndc: string;
  tier: number | null;
  full_cost: number | null;
  covered: boolean | null;
  initial_cost: number | null;
  estimated_yearly_total: number | null;
}
export interface FormularyRow {
  contract_id: string;
  plan_id: string;
  rxcui: string;
  tier: number | null;
  copay: number | null;
  coinsurance: number | null;
  prior_auth: boolean | null;
  step_therapy: boolean | null;
  quantity_limit: boolean | null;
  quantity_limit_amount: number | null;
  quantity_limit_days: number | null;
}
export interface NdcRow {
  rxcui: string;
  ndc: string;
  default_quantity_30: number | null;
  default_quantity_90: number | null;
}
export interface ProviderNetworkRow {
  plan_id: string;
  segment_id: string;
  npi: string;
  covered: boolean | null;
}
export interface PlanBrainData {
  // The catalog year these plans/benefits belong to, and whether CMS has
  // published that year's formulary yet (active cms_spuf_releases row). When
  // formularyPublished is false, formularyByContractPlan is empty NOT because
  // the drugs aren't covered but because the year's SPUF drug file isn't out —
  // the brain must not price drugs. Optional for backward-compat with any
  // cached/older response; treated as published when absent.
  planYear?: number;
  formularyPublished?: boolean;
  benefitsByPlan: Record<string, BenefitRow[]>;
  drugCostCache: Record<string, Record<string, DrugCostCacheRow>>;
  /** Per-plan IRA-selected-drug cost-share by tier (preferred pharmacy),
   *  keyed by triple id. Coinsurance as a 0..1 fraction. MFP-fallback only. */
  iraCostShareByPlan?: Record<string, Record<string, { type: 'copay' | 'coinsurance'; amount: number }>>;
  /** Per-plan CMS rx_deductible.exempt_tiers, keyed `${contract}-${plan}`
   *  (segment-agnostic). The Part D deductible applies to every tier NOT
   *  listed. Absent ⇒ the brain's legacy [3,4,5] default. */
  deductibleExemptTiersByPlan?: Record<string, number[]>;
  formularyByContractPlan: Record<string, Record<string, FormularyRow>>;
  ndcByRxcui: Record<string, NdcRow>;
  networkByPlan: Record<string, Record<string, ProviderNetworkRow>>;
  // Sellability — per-plan appointment/certification flags (keyed by the
  // triple id the client requested), plus the ranked plan-year's exclude
  // rules + carrier kill-switch flags. Drive the brain's pre-gate sellable
  // partition so non-sellable plans never enter the top four. Optional for
  // back-compat with older/cached responses that predate the field — when
  // absent the brain treats every plan as sellable.
  sellableByPlan?: Record<
    string,
    { is_appointed: boolean; is_certified: boolean; pays_commission: boolean }
  >;
  sellableRules?: readonly import('../../api/_lib/sellable-rules').SellableRule[];
  sellableFlags?: import('../../api/_lib/sellable-rules').SellableFlags;
  // Authoritative MAPD (has-Part-D) set for the ranked year, as
  // `${contract_id}-${plan_id}` strings (projection of pm_mapd_plan_set).
  // The brain drops any eligible plan NOT in this set for a non-VA
  // drug-having user. Optional for back-compat with older/cached responses;
  // absent or empty ⇒ MA-only filter suspended (fail-safe / pre-promotion).
  mapdContractPlanIds?: string[];
}

// Compat applied-rule shape — agent UI reads `action`/`reason`/`points`/`ruleId`.
// Maps from broker-rules.AppliedRule (which has no `action` field — every
// rule is a numeric `points` adjustment with sign). We reconstruct
// `action` from points sign so the UI's filter on "penalize" still works.
export interface CompatRuleApplication {
  ruleId: string;
  action: 'boost' | 'penalize' | 'flag';
  points: number;
  reason: string;
}

// Compat red-flag shape — agent UI reads `id`, `severity`, `message`,
// and filters for severity 'critical' || 'disqualify'. New brain emits
// `id` (RedFlagFamily), `severity`, `action`, `message`. We expose a
// pseudo-severity 'disqualify' when action === 'disqualify' so the
// agent's filter still triggers without rewriting it.
export interface CompatRedFlag {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'disqualify';
  action: 'disqualify' | 'penalize' | 'warn' | 'flag';
  message: string;
}

// Compat real-annual-cost shape. Maps from new AnnualCostEstimate
// (premium/drugCost/medicalCost/suppliesCost/erExpected/hospitalExpected/
// partBGivebackSavings/cappedMedicalBucket/netAnnual) to the old name
// scheme expected by quotePdf and useAgentBaseRecommend.
export interface CompatRealAnnualCost {
  premium: number;
  drugs: number;
  medicalVisits: number;
  supplies: number;
  erExpected: number;
  hospitalExpected: number;
  givebackSavings: number;
  medicalGross: number;
  medicalCapped: number;
  cappedAtMoop: boolean;
  netAnnual: number;
}

// Compat utilization profile shape used by quotePdf for the assumption
// block. Maps AnnualUtilization → the old keys (pcp/specialist/labs/
// erProbability/hospitalProbability/monthlySupplies/hospitalDays).
export interface CompatUtilizationProfile {
  pcp: number;
  specialist: number;
  labs: number;
  erProbability: number;
  hospitalProbability: number;
  hospitalDays: number;
  monthlySupplies: number;
}

// Compat detected-condition. Mirrors the old shape that included
// brokerImplications + a typed condition key.
export interface CompatDetectedCondition {
  condition: DetectedConditionKey;
  confidence: 'certain' | 'likely' | 'possible';
  triggerMeds: ReadonlyArray<string>;
  brokerImplications: ReadonlyArray<string>;
}

// Compat medication pattern — agent UI reads p.id + p.severity + p.summary.
// Maps from new MedicationPattern (id/variant/implication).
export interface CompatMedicationPattern {
  id: string;
  variant: string;
  severity: 'low';
  summary: string;
}

export interface CompatArchetype {
  archetype: string;
  label: string;
  description: string;
}

export interface ScoredPlan {
  plan: Plan;
  rank: number;
  composite: number;
  drugScore: number;
  oopScore: number;
  extrasScore: number;
  totalAnnualDrugCost: number;
  annualMedicalCost: number;
  totalOOPEstimate: number;
  extrasValue: number;
  providerBoost: number;
  providerNetworkStatus: 'all_in' | 'partial' | 'all_out' | 'unknown';
  uncoveredDrugRxcuis: string[];
  /** Mirror of BrainScore.drugCoverageUnknown — true when at least one
   *  user drug has no pm_drug_cost_cache row AND isn't on the plan's
   *  formulary. UI surfaces the "drug coverage estimated — confirm
   *  with your pharmacist" disclaimer on affected plan columns. */
  drugCoverageUnknown: boolean;
  /** Mirror of BrainScore.drugCostsUnavailable — true when the catalog
   *  year's formulary isn't published yet (CMS SPUF not posted) AND the
   *  user has drugs. When true, drug costs are $0 and EXCLUDED from the
   *  total: the UI must render "formulary not yet published" per drug and
   *  label any drug-inclusive total incomplete (or hide it). Never both
   *  this and drugCoverageUnknown. Same value for every plan in a run. */
  drugCostsUnavailable: boolean;
  /** Count of user drugs the plan's formulary actually files. Wired
   *  directly from BrainScore.coveredCount so UI doesn't have to
   *  re-query plan.formulary (which is `{}` in agent-v3 because no one
   *  hydrates it). */
  drugsCovered: number;
  /** Total user drugs evaluated. Matches userProfile.drugs.length. */
  drugsTotal: number;
  /** Per-medication breakdown — pass-through from
   *  BrainScore.drugBreakdown. One entry per user drug, in input
   *  order. CompareScreen renders per-med rows in each SlotCell
   *  ("Synthroid · Tier 1 · $3/mo · $36/yr") and a "$X/yr (Y/Z
   *  covered)" one-liner on the bench. Empty when no user drugs. */
  drugBreakdown: ReadonlyArray<{
    rxcui: string;
    name: string;
    covered: boolean;
    tier: number | null;
    monthlyCopay: number | null;
    annualCost: number;
    /** True when the catalog year's formulary isn't published yet. The row
     *  carries NO real tier or cost — render "formulary not yet published",
     *  never $0 / Tier / Not covered. */
    costUnavailable?: boolean;
  }>;
  ribbon: RibbonKey | null;
  breakdown: string;
  drugCostByRxcui: Record<string, number>;
  appliedRules: CompatRuleApplication[];
  brokerRuleAdjustment: number;
  isCsnp: boolean;
  /** True when force-inserted into the Top 4 by the C-SNP reserved-
   *  slot pass (see plan-brain.ts). UI badges this as "Recommended
   *  for your condition." */
  csnpReservedSlot: boolean;
  /** DEPRECATED — always false after the strict-gates rewrite. Gate 2
   *  now hard-eliminates any plan missing a user drug; no medication
   *  backfill ever fires. Field kept for caller compat. */
  medicationBackfill: boolean;
  realAnnualCost: CompatRealAnnualCost | null;
  /** Passed through from BrainScore.dualEligibleAdjustment when the
   *  client answered any Medicaid / LIS question on IntakeScreen.
   *  When present, `realAnnualCost` and `totalAnnualDrugCost` above
   *  are ALREADY adjusted — the pre-adjustment snapshot lives on
   *  `.original` for strikethrough rendering on the Compare surfaces. */
  dualEligibleAdjustment?: DualEligibleAdjustment;
  redFlags: CompatRedFlag[];
  disqualified: boolean;
  whySwitchCopy: string;
  /** Underlying combined utilization for this plan (same per run). */
  annualUtilization: AnnualUtilization;
  /** Per-gate survivorship. Mirrors BrainScore.gate*Passed populated
   *  by runPlanBrain after each gate phase. CompareScreen's bench
   *  reads these to label why an eliminated plan didn't make Top 4
   *  ("Provider OON" / "Meds not covered" / "Missing benefit"). */
  gate_results: {
    gate1_passed: boolean;
    gate2_passed: boolean;
    gate3_passed: boolean;
  };
  /** Sellability — false ⇒ the plan is non-commissionable (not
   *  appointed/certified, UHC/Devoted flag, exclude rule, or Wellcare
   *  non-allowlist) and was held off the top four; it only ever appears on
   *  the bench. Mirrors BrainScore.sellable. Absent/true ⇒ sellable. */
  sellable?: boolean;
  sellableReason?: string | null;
  /** Per-gate customer-facing micro-explainer strings — pass-through
   *  from BrainScore.explanations. One entry per user-supplied
   *  provider / drug / priority on gates 1–3; a single cost-rank line
   *  on gate 4. Empty arrays mean the gate didn't apply.
   *  CompareScreen reads this via explanationsByPlanId to render the
   *  collapsible "Why this plan" pill list on each SlotCell. */
  explanations: {
    gate1: ReadonlyArray<string>;
    gate2: ReadonlyArray<string>;
    gate3: ReadonlyArray<string>;
    gate4: string;
  };
}

export interface PlanBrainResult {
  population: Population;
  weights: WeightProfile;
  utilization: 'low' | 'moderate' | 'high';
  utilizationProfile: CompatUtilizationProfile;
  scored: ScoredPlan[];
  /** Every plan in the eligible pool that did NOT make Top 4, sorted
   *  by total annual cost ascending. Each entry carries gate_results
   *  so CompareScreen can label the elimination reason. Useful for
   *  the broker who wants to scroll past the 4-up and see the full
   *  county pool with their elimination footprint. */
  bench: ScoredPlan[];
  filteredOut: { plan: Plan; reason: string }[];
  detectedConditions: CompatDetectedCondition[];
  medicationPatterns: CompatMedicationPattern[];
  archetype: CompatArchetype;
  /** Set when the user qualifies for a C-SNP but no C-SNP plan in
   *  this county passed Gates 1+2. UI surfaces this as a context
   *  note explaining why the Top 4 contains zero C-SNPs. Null when
   *  the user did not qualify or a C-SNP is in the Top 4. */
  csnpNote: string | null;
  /** User drugs the brain could not evaluate because they arrived
   *  without a resolvable rxcui (typically CRM free-text entries
   *  that never routed through the drug-search autocomplete). The
   *  Compare screen renders a data-quality banner listing these
   *  names when the array is non-empty — rankings are computed with
   *  these drugs bypassed at Gate 2 to avoid an empty pool, but
   *  they DID NOT participate in coverage elimination. Empty when
   *  every user drug has an rxcui or the user has no drugs. */
  unresolvedDrugs: ReadonlyArray<{ index: number; name: string }>;
}

// ─── Adapter input ──────────────────────────────────────────────────

interface Args {
  plans: Plan[];
  client: Client;
  medications: Medication[];
  providers: Provider[];
  conditionProfile?: ConditionKey | null;
  userPriorities?: string[];
  populationOverride?: Population | null;
  weightOverride?: Partial<WeightProfile> | null;
}

interface State {
  result: PlanBrainResult | null;
  /** Live Medicare.gov gross monthly price per rxcui (null until fetched
   *  or on failure). Shared with useRankedPlans so the Compare cards price
   *  drugs off the same real prices. */
  grossMonthlyByRxcui: Map<string, number> | null;
  /** Raw aggregated Brain data — exposes per-drug, per-medical, and
   *  per-network rows so a consumer (e.g. the v4 quote table) can
   *  render exact dollar values instead of just the composite score.
   *  Null until the first fetch lands. */
  data: PlanBrainData | null;
  loading: boolean;
  error: string | null;
  /** True when (a) plans, (b) /api/plan-brain-data has returned, and
   *  (c) we're not currently fetching. The brain runs ONCE per stable
   *  input set after this is true; consumers should show a loading
   *  state until then to avoid rendering a partial scoring pass that
   *  flickers as provider-network rows arrive late. */
  ready: boolean;
  /** Names of providers entered without an NPI that NPPES could not
   *  resolve. Consumers MUST surface this — when non-empty, Gate 1 had
   *  no NPI to check for those providers, so the ranking does not
   *  reflect their network status. */
  unresolvedProviderNames: string[];
}

// ─── PmPlanRow synthesis from agent's Plan ──────────────────────────
// The agent's Plan record is the source-of-truth for premium / MOOP /
// star / formulary etc. The brain wants a PmPlanRow (a flatter,
// CMS-style record). We build one synthetic PmPlanRow per Plan; fields
// the agent doesn't carry (county_fips, snp_type, sanctioned, enrollment_*)
// get sensible defaults — those drive a few brain heuristics but never
// any cost/extras output, so safe to stub.
function planToPmRow(
  plan: Plan,
  county: string,
  idx: number,
  sellableFlags?: { is_appointed: boolean; is_certified: boolean; pays_commission: boolean },
): PmPlanRow {
  // Plan.id is the canonical "<contract>-<plan>-<segment>" triple
  // (e.g. "H1036-308-0"). Split it back out for the row.
  const parts = plan.id.split('-');
  const contract = parts[0] ?? plan.contract_id;
  const planNum = parts[1] ?? plan.plan_number;
  const segRaw = parts[2] ?? '0';
  // pm_plans stores the segment as 0/1/2/etc (no zero padding) — keep
  // that representation so brain key generation matches the agent's
  // existing triple ids verbatim.
  const segment = segRaw.replace(/^0+/, '') || '0';
  // SNP type is carried through the agent's PlanType enum. classifySnp
  // in plan-brain.ts substring-matches on snp_type, so stamp the canonical
  // hyphenated form ("C-SNP" / "D-SNP" / "I-SNP") rather than the enum
  // value so the brain bucketing matches the pm_plans schema.
  const snpKind: 'C-SNP' | 'D-SNP' | 'I-SNP' | null =
    plan.plan_type === 'DSNP' ? 'D-SNP'
    : plan.plan_type === 'CSNP' ? 'C-SNP'
    : plan.plan_type === 'ISNP' ? 'I-SNP'
    : null;
  const isSnp = snpKind != null;
  return {
    id: idx,
    contract_id: contract,
    plan_id: planNum,
    segment_id: segment,
    plan_name: plan.plan_name,
    carrier: plan.carrier,
    parent_organization: null,
    plan_type: plan.plan_type,
    state: plan.state,
    county_name: county,
    county_fips: null,
    monthly_premium: plan.premium,
    annual_deductible: plan.annual_deductible ?? 0,
    moop: plan.moop_in_network,
    drug_deductible: plan.drug_deductible,
    star_rating: plan.star_rating,
    snp: isSnp,
    snp_type: snpKind,
    sanctioned: false,
    enrollment_count: null,
    enrollment_as_of: null,
    // Appointment/certification from /api/plan-brain-data's sellableByPlan.
    // Absent (no row) ⇒ undefined ⇒ sellable (State A) in the brain's filter.
    // pays_commission is carried for the record only — never read for ranking.
    is_appointed: sellableFlags?.is_appointed,
    is_certified: sellableFlags?.is_certified,
    pays_commission: sellableFlags?.pays_commission,
  };
}

// ─── BenefitRow → PlanBenefitRow translation ────────────────────────
// Agent's benefit row uses (benefit_type, tier_id, description, source)
// while consumer's PlanBenefitRow uses (benefit_category, benefit_description,
// coverage_amount, max_coverage). Map field-by-field; missing data
// becomes null. tier_id is encoded into the synthetic id so React
// keys stay unique when the brain rebuilds the list.
function benefitToBrain(
  contract: string,
  planNum: string,
  segment: string,
  row: BenefitRow,
  i: number,
): PlanBenefitRow {
  const allowedSource =
    row.source === 'medicare_gov' || row.source === 'pbp_federal'
      ? 'pbp'
      : row.source === 'sb_ocr' || row.source === 'manual'
        ? row.source
        : 'pbp';
  // alt_ passes through raw — PlanBenefitRow.alt_source's union
  // accepts every raw source name (medicare_gov / cms_pbp /
  // pbp_federal) plus the normalized landscape / pbp / sb_ocr /
  // manual set, because the population-aware picker in
  // selectCostShare needs to match on cms_pbp specifically to know
  // "this was the plan's filed value for protected populations."
  // Normalizing to 'pbp' here would erase that distinction.
  const rawAltSource = (row.alt_source ?? null) as
    | 'landscape'
    | 'pbp'
    | 'sb_ocr'
    | 'manual'
    | 'medicare_gov'
    | 'cms_pbp'
    | 'pbp_federal'
    | null;
  return {
    id: `agent:${contract}-${planNum}-${segment}:${row.benefit_type}:${row.tier_id ?? i}`,
    contract_id: contract,
    plan_id: planNum,
    segment_id: segment,
    benefit_category: row.tier_id
      ? `${row.benefit_type}_${row.tier_id}`
      : row.benefit_type,
    benefit_description: row.description,
    coverage_amount: null,
    copay: row.copay,
    coinsurance: row.coinsurance,
    max_coverage: null,
    source: allowedSource as 'pbp' | 'sb_ocr' | 'manual',
    alt_copay: row.alt_copay ?? null,
    alt_coinsurance: row.alt_coinsurance ?? null,
    alt_source: rawAltSource,
  };
}

// ─── FormularyRow → FormularyCoverage translation ───────────────────
function formularyToBrain(row: FormularyRow): FormularyCoverage {
  return {
    rxcui: row.rxcui,
    drug_name: null,
    tier: row.tier,
    copay: row.copay,
    coinsurance: row.coinsurance,
    prior_auth: row.prior_auth === true,
    step_therapy: row.step_therapy === true,
    quantity_limit: row.quantity_limit === true,
    quantity_limit_amount: row.quantity_limit_amount ?? null,
    quantity_limit_days: row.quantity_limit_days ?? null,
    match_type: 'rxcui',
  };
}

// Map agent ConditionKey → CsnpCondition where overlap exists. The
// brain's csnpConditions is consumed for SNP plan-pool filtering and
// utilization; agent's ConditionKey covers a similar domain with
// minor name mismatches.
function conditionToCsnp(c: ConditionKey | null | undefined): CsnpCondition | null {
  switch (c) {
    case 'diabetes':
      return 'diabetes';
    case 'chf':
      return 'cardio';
    case 'copd':
      return 'copd';
    case 'hypertension':
      return 'hypertension';
    case 'ckd':
      return 'esrd';
    default:
      return null;
  }
}

export interface AdapterArgs {
  plans: Plan[];
  client: Client;
  medications: Medication[];
  providers: Provider[];
  data: PlanBrainData;
  conditionProfile?: ConditionKey | null;
  userPriorities?: string[];
  weightsOverride?: BrainWeights | null;
  /** rxcui → live Medicare.gov gross monthly price (see drugGrossPrice). */
  grossMonthlyByRxcui?: Map<string, number>;
}

// Exported for Node-side smoke harnesses only. Do not use from React
// components — call the usePlanBrain hook instead.
export function adaptToBrainInputs(args: AdapterArgs): BrainInputs {
  const { plans, client, medications, providers, data } = args;
  const county = client.county || '';
  // Per-plan appointment/certification flags from /api/plan-brain-data,
  // keyed by the plan's triple id (same id the hook requested). Absent ⇒
  // the plan has no pm_plan_sellability row ⇒ sellable (State A).
  const sellableByPlan = data.sellableByPlan ?? {};
  const planRows: PmPlanRow[] = plans.map((p, i) =>
    planToPmRow(p, county, i + 1, sellableByPlan[p.id]),
  );

  // ── planByKey (aggregated Plan objects) ──────────────────────────
  // Brain-side extras readers prefer these over the raw PlanBenefitRow[]
  // path because benefitToBrain hardcodes coverage_amount/max_coverage
  // to null (extras filed by CMS as allowances land on row.copay in
  // pbp_benefits_v2 and /api/plans buildBenefits already normalizes them
  // into PlanBenefits fields like dental.annual_max).
  // Key format matches planKeyWithSegment: contract-plan-segment with
  // leading zeros stripped from the segment.
  const planByKey = new Map<string, Plan>();
  for (const p of plans) {
    const parts = p.id.split('-');
    const contract = parts[0] ?? p.contract_id;
    const planNum = parts[1] ?? p.plan_number;
    const segRaw = (parts[2] ?? '0').replace(/^0+/, '') || '0';
    planByKey.set(`${contract}-${planNum}-${segRaw}`, p);
  }

  // ── benefitsByPlanKey ─────────────────────────────────────────────
  // agent payload: `${contract}-${plan}-${segment}` (one zero-padded
  // segment form) → BenefitRow[]. Brain expects the same triple key.
  const benefitsByPlanKey = new Map<string, PlanBenefitRow[]>();
  for (const [tripleId, rows] of Object.entries(data.benefitsByPlan)) {
    const parts = tripleId.split('-');
    const contract = parts[0] ?? '';
    const planNum = parts[1] ?? '';
    const segRaw = (parts[2] ?? '0').replace(/^0+/, '') || '0';
    // Brain keying convention: `${contract}-${plan}-${segment}` (no zero pad).
    const key = `${contract}-${planNum}-${segRaw}`;
    benefitsByPlanKey.set(
      key,
      rows.map((r, i) => benefitToBrain(contract, planNum, segRaw, r, i)),
    );
  }

  // ── formularyByPlanKey (no segment) ───────────────────────────────
  const formularyByPlanKey = new Map<string, Map<string, FormularyCoverage>>();
  for (const [contractPlan, rxMap] of Object.entries(data.formularyByContractPlan)) {
    const inner = new Map<string, FormularyCoverage>();
    for (const [rxcui, row] of Object.entries(rxMap)) {
      inner.set(rxcui, formularyToBrain(row));
    }
    formularyByPlanKey.set(contractPlan, inner);
  }

  // ── mapdContractPlanIds (MA-only exclusion) ───────────────────────
  // The authoritative MAPD (has-Part-D) set for the ranked year, sourced by
  // /api/plan-brain-data from pm_mapd_plan_set (a projection of distinct
  // (plan_year, contract_id, plan_id) that have a Part D formulary). The
  // brain drops any eligible plan NOT in this set for a non-VA drug-having
  // user — see the MA-only filter in plan-brain.ts (gated on size > 0).
  //
  // CORRECTION (was previously hardcoded undefined): the old comment claimed
  // /api/plans' planType=MAPD query pre-filter already removed MA-only plans,
  // making this redundant. It does NOT — api/plans.ts mapPlanType() maps every
  // non-PDP/non-SNP plan_type (including HMO-POS) to 'MAPD', so a medical-only
  // plan with NO Part D (e.g. Blue Medicare Medical Only H3449-012,
  // drug_deductible=null, absent from pm_mapd_plan_set) passes the planType
  // filter and would otherwise rank #1 for a drug-having user. The consumer
  // excludes it via this exact set; the agent now mirrors that.
  //
  // This is NOT the old rxcui-scoped derivation (which under-counted MAPDs and
  // collapsed Durham to 2 plans) — pm_mapd_plan_set is the full per-year MAPD
  // projection, independent of the user's drug list. Empty set (failed/pre-
  // promotion load) ⇒ the brain's size>0 gate suspends the filter (fail-safe).
  const mapdContractPlanIds: ReadonlySet<string> | undefined =
    data.mapdContractPlanIds ? new Set(data.mapdContractPlanIds) : undefined;

  // ── providerNetworkByPlanKey (keyed contract-plan, no segment) ───
  const providerNetworkByPlanKey = new Map<
    string,
    Map<string, { npi: string; covered: boolean }>
  >();
  for (const [tripleId, npiMap] of Object.entries(data.networkByPlan)) {
    const parts = tripleId.split('-');
    const contract = parts[0] ?? '';
    const planNum = parts[1] ?? '';
    const cpKey = `${contract}-${planNum}`;
    const inner = providerNetworkByPlanKey.get(cpKey) ?? new Map();
    for (const [npi, row] of Object.entries(npiMap)) {
      inner.set(npi, { npi, covered: row.covered === true });
    }
    providerNetworkByPlanKey.set(cpKey, inner);
  }

  // ── drugCostCacheByPlanKey (triple-id → ndc → entry) ──────────────
  type CacheEntry = {
    ndc: string;
    tier: number | null;
    full_cost: number | null;
    // Real per-fill member cost from pm_drug_cost_cache — drives the brain's
    // IRA-drug pricing (partDTimeline initial-phase override). Dropping it
    // here silently reverts the cost-engine fix on the agent surface.
    initial_cost: number | null;
    estimated_yearly_total: number | null;
    covered: boolean;
  };
  const drugCostCacheByPlanKey = new Map<string, Map<string, CacheEntry>>();
  for (const [tripleId, ndcMap] of Object.entries(data.drugCostCache)) {
    const parts = tripleId.split('-');
    const contract = parts[0] ?? '';
    const planNum = parts[1] ?? '';
    const segRaw = (parts[2] ?? '0').replace(/^0+/, '') || '0';
    const key = `${contract}-${planNum}-${segRaw}`;
    const inner = new Map<string, CacheEntry>();
    for (const [ndc, row] of Object.entries(ndcMap)) {
      inner.set(ndc, {
        ndc,
        tier: row.tier,
        full_cost: row.full_cost,
        initial_cost: row.initial_cost,
        estimated_yearly_total: row.estimated_yearly_total,
        covered: row.covered === true,
      });
    }
    drugCostCacheByPlanKey.set(key, inner);
  }

  // ── iraCostShareByPlanKey (triple-id → tier → IRA cost-share) ──────
  // Feeds the MFP fallback: an IRA-negotiated drug with no cache row is
  // priced at its Maximum Fair Price with the plan's IRA cost-share.
  const iraCostShareByPlanKey = new Map<
    string,
    Map<number, { type: 'copay' | 'coinsurance'; amount: number }>
  >();
  for (const [tripleId, tierMap] of Object.entries(data.iraCostShareByPlan ?? {})) {
    const parts = tripleId.split('-');
    const key = `${parts[0] ?? ''}-${parts[1] ?? ''}-${(parts[2] ?? '0').replace(/^0+/, '') || '0'}`;
    const inner = new Map<number, { type: 'copay' | 'coinsurance'; amount: number }>();
    for (const [tier, share] of Object.entries(tierMap)) inner.set(Number(tier), share);
    iraCostShareByPlanKey.set(key, inner);
  }

  // ── deductibleExemptTiersByPlanKey (contract-plan → exempt tiers) ──
  // Segment-agnostic CMS rx_deductible.exempt_tiers; the brain looks up
  // with `${contract}-${plan}`.
  const deductibleExemptTiersByPlanKey = new Map<string, number[]>();
  for (const [key, tiers] of Object.entries(data.deductibleExemptTiersByPlan ?? {})) {
    if (Array.isArray(tiers)) deductibleExemptTiersByPlanKey.set(key, tiers);
  }

  // ── rxcuiToNdc bridge ─────────────────────────────────────────────
  const rxcuiToNdc = new Map<string, string>();
  for (const [rxcui, row] of Object.entries(data.ndcByRxcui)) {
    rxcuiToNdc.set(rxcui, row.ndc);
  }

  // ── userProfile ───────────────────────────────────────────────────
  const conditionCsnp = conditionToCsnp(args.conditionProfile);
  const sepCode = client.sepReasonCode;

  // SEP auto-set cascade — non-destructive: explicit values win, sepCode
  // only fills in when the client hasn't already answered upstream.
  // LTC (leaving nursing home / rehab) → institutional_or_hcbs, which
  // flips LIS tier to full_institutional for FBDE beneficiaries.
  const derivedLivingSetting =
    client.livingSetting ?? (sepCode === 'LTC' ? 'institutional_or_hcbs' : 'community');

  // MCD (just qualified for Medicaid / Extra Help) → dsnpEligible.
  // Cascades alongside the existing explicit flag / medicaidLevel /
  // legacy medicaidConfirmed checks.
  const dsnpFromSep = sepCode === 'MCD';

  const userProfile: UserProfile = {
    drugs: medications.map((m) => ({ rxcui: m.rxcui, name: m.name, isBrand: m.isBrand ?? false })),
    providers: providers.map((p) => ({ npi: p.npi, name: p.name })),
    priorities: new Set(args.userPriorities ?? []),
    // dsnpEligible: prefer the explicit client.dsnpEligible flag (set
    // by the Intake pill), else fall back to medicaidLevel (any non-
    // 'none' category means dual), else the MCD SEP auto-set, else the
    // legacy medicaidConfirmed checkbox from the older Step2Intake path.
    dsnpEligible:
      client.dsnpEligible === true ||
      (client.medicaidLevel != null && client.medicaidLevel !== 'none') ||
      dsnpFromSep ||
      client.medicaidConfirmed === true
        ? true
        : null,
    csnpConditions: conditionCsnp ? [conditionCsnp] : [],
    age: ageFromDob(client.dob),
    hasVaDrugCoverage: client.hasVaDrugCoverage === true,
    // Dual-eligible / LIS wiring — activates the brain's
    // applyDualEligibleCostAdjustment. Defaults keep the adjustment
    // a no-op for older sessions that haven't captured these fields.
    medicaidLevel: client.medicaidLevel ?? 'none',
    // normalizeLisTier re-derives the tier when a session was persisted
    // with the retired `qmb_uniform` value (2026-07-23 → 2026-08-15).
    // Derived from medicaidLevel rather than mapped from the stale tier,
    // because qmb_uniform was written for both full-benefit duals in the
    // community (→ full_low) and standalone QMB (→ full_high); guessing
    // would understate drug cost for the QMB-only case.
    lisTier: normalizeLisTier(
      client.lisTier,
      client.medicaidLevel ?? 'none',
      derivedLivingSetting,
    ),
    livingSetting: derivedLivingSetting,
  };

  return {
    plans: planRows,
    benefitsByPlanKey,
    formularyByPlanKey,
    planByKey,
    userProfile,
    county: county || null,
    drugCostCacheByPlanKey,
    rxcuiToNdc,
    iraCostShareByPlanKey,
    deductibleExemptTiersByPlanKey,
    grossMonthlyByRxcui: args.grossMonthlyByRxcui,
    providerNetworkByPlanKey,
    mapdContractPlanIds,
    weightsOverride: args.weightsOverride ?? undefined,
    enrollmentPeriod: client.enrollmentPeriod,
    sepReasonCode: client.sepReasonCode,
    // Carry the formulary-publication state so the brain refuses to price
    // drugs for a year whose SPUF drug file CMS hasn't posted yet.
    formularyPublished: data.formularyPublished,
    planYear: data.planYear,
    // Sellability — the ranked year's exclude rules + carrier kill-switch
    // flags. When present (every live /api/plan-brain-data response), the
    // brain tags each plan sellable before the gates and benches the
    // non-sellable ones so the local top four match the consumer. Absent on
    // older/cached responses ⇒ every plan sellable (back-compat).
    sellableRules: data.sellableRules,
    sellableFlags: data.sellableFlags,
    now: new Date(),
  };
}

function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a;
}

// ─── BrainOutput → compat PlanBrainResult ───────────────────────────

function pmRowToPlanId(row: PmPlanRow): string {
  // Match Plan.id format: "<contract>-<plan>-<segment>" with NO zero pad.
  return `${row.contract_id}-${row.plan_id}-${row.segment_id}`;
}

function adaptScored(
  brain: BrainScoredPlan,
  rank: number,
  agentPlanByTriple: Map<string, Plan>,
): ScoredPlan | null {
  const planId = pmRowToPlanId(brain.row);
  const plan = agentPlanByTriple.get(planId);
  if (!plan) return null;

  const score = brain.score;
  const realAnnual: CompatRealAnnualCost | null = score.realAnnualCost
    ? mapRealAnnualCost(score.realAnnualCost)
    : null;

  // After Gate 1's relaxation, plans with any unverified provider pass
  // through with score.allProvidersOutOfNetwork=true (when inNetCount
  // is 0). Map unverified BEFORE all_out so the UI badges 'unknown' and
  // the broker knows to call the carrier — not 'all_out' which reads
  // as "we confirmed your doctor is out of network."
  const providerNetworkStatus: ScoredPlan['providerNetworkStatus'] =
    score.allProvidersInNetwork
      ? 'all_in'
      : score.anyProviderUnverified
        ? 'unknown'
        : score.allProvidersOutOfNetwork
          ? 'all_out'
          : score.anyProviderOutOfNetwork
            ? 'partial'
            : 'unknown';

  const appliedRules: CompatRuleApplication[] = score.appliedBrokerRules.map((r) => ({
    ruleId: r.ruleId,
    action: r.points > 0 ? 'boost' : r.points < 0 ? 'penalize' : 'flag',
    points: Math.abs(r.points),
    reason: r.reason,
  }));

  const brokerRuleAdjustment = score.appliedBrokerRules.reduce(
    (sum, r) => sum + r.points,
    0,
  );

  const redFlags: CompatRedFlag[] = score.redFlags.map((f) => ({
    id: f.id,
    severity: f.action === 'disqualify' ? 'disqualify' : f.severity,
    action: f.action,
    message: f.message,
  }));

  const isCsnp = (() => {
    const t = (brain.row.snp_type ?? brain.row.plan_type ?? '').toLowerCase();
    return t.includes('c-snp') || t.includes('csnp') || t.includes('chronic');
  })();

  const uncoveredDrugRxcuis: string[] = [];
  // Brain doesn't surface a per-rxcui uncovered list directly; derive
  // from coveredCount vs totalCount when the user has drugs.
  // (Empty when totalCount === 0 — nothing to mark uncovered.)

  return {
    plan,
    rank,
    composite: score.composite,
    drugScore: score.drugCostScore,
    oopScore: score.oopCostScore,
    extrasScore: score.extraBenefitsScore,
    totalAnnualDrugCost: score.totalAnnualDrugCost,
    annualMedicalCost: score.annualMedicalCost,
    totalOOPEstimate: score.totalOOPEstimate,
    extrasValue: score.extrasValueAnnual,
    providerBoost: score.allProvidersInNetwork
      ? 5
      : score.anyProviderOutOfNetwork
        ? -10
        : 0,
    providerNetworkStatus,
    uncoveredDrugRxcuis,
    drugCoverageUnknown: score.drugCoverageUnknown,
    drugCostsUnavailable: score.drugCostsUnavailable,
    drugsCovered: score.coveredCount,
    drugsTotal: score.totalCount,
    drugBreakdown: score.drugBreakdown,
    ribbon: score.ribbon,
    breakdown: score.costBreakdown,
    drugCostByRxcui: {},
    appliedRules,
    brokerRuleAdjustment,
    isCsnp,
    csnpReservedSlot: score.csnpReservedSlot,
    medicationBackfill: score.medicationBackfill,
    realAnnualCost: realAnnual,
    dualEligibleAdjustment: score.dualEligibleAdjustment,
    redFlags,
    // No longer treats unverified-network plans as disqualified — Gate 1
    // passes Unknown with a flag, and QuoteDeliveryV4 filters on this
    // term to hide plans from the audit columns.
    disqualified: score.disqualifiedByRedFlag,
    whySwitchCopy: score.costBreakdown,
    annualUtilization: score.annualUtilization,
    gate_results: {
      gate1_passed: score.gate1Passed,
      gate2_passed: score.gate2Passed,
      gate3_passed: score.gate3Passed,
    },
    sellable: score.sellable,
    sellableReason: score.sellableReason,
    explanations: {
      gate1: score.explanations.gate1,
      gate2: score.explanations.gate2,
      gate3: score.explanations.gate3,
      gate4: score.explanations.gate4,
    },
  };
}

function mapRealAnnualCost(c: AnnualCostEstimate): CompatRealAnnualCost {
  // Old shape splits drug + medical visits + supplies + ER + hospital
  // separately; new shape collapses ER/hospital probability into
  // erExpected/hospitalExpected and "medicalCost" into a single
  // pre-cap bucket. Map field-by-field:
  return {
    premium: Math.round(c.premium),
    drugs: Math.round(c.drugCost),
    medicalVisits: Math.round(c.medicalCost),
    supplies: Math.round(c.suppliesCost),
    erExpected: Math.round(c.erExpected),
    hospitalExpected: Math.round(c.hospitalExpected),
    givebackSavings: Math.round(c.partBGivebackSavings),
    medicalGross: Math.round(
      c.medicalCost + c.suppliesCost + c.erExpected + c.hospitalExpected,
    ),
    medicalCapped: Math.round(c.cappedMedicalBucket),
    cappedAtMoop:
      c.cappedMedicalBucket <
      c.medicalCost + c.suppliesCost + c.erExpected + c.hospitalExpected,
    netAnnual: Math.round(c.netAnnual),
  };
}

function adaptUtilization(u: AnnualUtilization): CompatUtilizationProfile {
  return {
    pcp: u.pcpVisits,
    specialist: u.specialistVisits,
    labs: u.labDraws,
    erProbability: u.erProbability,
    hospitalProbability: u.hospitalProbability,
    hospitalDays: u.hospitalDays,
    monthlySupplies: u.diabeticSuppliesMonths,
  };
}

function adaptPopulation(p: RankPopulation): Population {
  switch (p) {
    case 'csnp':
      return 'csnp';
    case 'dsnp':
    case 'dsnp-unsure':
      return 'dsnp';
    case 'standard':
    default:
      return 'mapd';
  }
}

function adaptDetectedConditions(
  list: BrainOutput['detectedConditions'],
): CompatDetectedCondition[] {
  return list.map((d) => {
    const key = d.condition as DetectedConditionKey;
    return {
      condition: key,
      confidence: d.confidence,
      triggerMeds: d.triggerMeds,
      brokerImplications: BROKER_IMPLICATIONS[key] ?? [],
    };
  });
}

const ARCHETYPE_LABEL: Record<string, { label: string; description: string }> = {
  specialty_drug: {
    label: 'Specialty drug user',
    description:
      'Tier-5 specialty drug economics dominate plan choice. MOOP and coinsurance % drive total cost.',
  },
  dual_eligible: {
    label: 'Dual eligible',
    description:
      'Medicare + Medicaid — D-SNP plans dominate. Extras (transportation, OTC, meals) are the tiebreaker.',
  },
  insulin_dependent: {
    label: 'Insulin-dependent',
    description:
      'IRA $35/mo insulin cap is mandatory; supplies coverage is the next biggest lever.',
  },
  complex_polypharmacy: {
    label: 'Polypharmacy (5+ meds)',
    description:
      'Drug coverage trumps everything else — one missing drug can flip the cheapest plan to the most expensive.',
  },
  multi_chronic: {
    label: 'Multiple chronic conditions',
    description:
      'MOOP is king — these patients WILL hit MOOP through hospitalization.',
  },
  single_chronic: {
    label: 'Single chronic condition',
    description:
      'Drug + OOP equally important. C-SNP if available — usually $0 copays on condition meds.',
  },
  provider_locked: {
    label: 'Provider-locked',
    description:
      'Multiple providers to keep — PPO or strong HMO network is required.',
  },
  healthy_newly_eligible: {
    label: 'Healthy / newly eligible',
    description:
      'Year-one extras dominate. Giveback, OTC, dental, vision, fitness.',
  },
  healthy_established: {
    label: 'Healthy / established',
    description:
      'Same shape as newly-eligible but with slightly more weight on OOP.',
  },
  general: {
    label: 'General Medicare beneficiary',
    description: 'Drug + OOP + extras balanced — no specific archetype detected.',
  },
};

function adaptArchetype(a: BrainOutput['archetype']): CompatArchetype {
  const meta = ARCHETYPE_LABEL[a] ?? ARCHETYPE_LABEL.general;
  return { archetype: a, label: meta.label, description: meta.description };
}

function adaptMedicationPatterns(
  list: BrainOutput['medicationPatterns'],
): CompatMedicationPattern[] {
  return list.map((p) => ({
    id: p.id,
    variant: p.variant,
    severity: 'low',
    summary: `${p.id.replace(/_/g, ' ')}: ${p.implication}`,
  }));
}

function adaptBrainOutput(
  brain: BrainOutput,
  agentPlanByTriple: Map<string, Plan>,
): PlanBrainResult {
  // brain.ranked is the FULL rawScored pool sorted by cost — it still
  // contains plans Gate 1 (provider OON / unverified) and Gate 2 (any
  // user drug not on formulary) eliminated. Mapping all of it into
  // `scored` is what surfaced plans with "0/1 doctors in-network" and
  // "Not available" drug costs as Top Pick. Match the consumer brain's
  // Results.tsx contract: iterate liveTop3.picks (the diversified
  // Gate-survivors in slot order — full_match → near_miss → C-SNP
  // reserved swap) and adapt only those. No backfill, no value
  // alternatives — if liveTop3 is null or has 2 picks, scored has 0
  // or 2 entries and CompareScreen renders that many.
  const scoredByKey = new Map<string, BrainScoredPlan>();
  for (const sp of brain.ranked) {
    scoredByKey.set(
      `${sp.row.contract_id}-${sp.row.plan_id}-${sp.row.segment_id}`,
      sp,
    );
  }
  const scored: ScoredPlan[] = [];
  const scoredKeys = new Set<string>();
  let rank = 1;
  for (const pick of brain.liveTop3?.picks ?? []) {
    const key = `${pick.plan.row.contract_id}-${pick.plan.row.plan_id}-${pick.plan.row.segment_id}`;
    const sp = scoredByKey.get(key);
    if (!sp) continue;
    const adapted = adaptScored(sp, rank, agentPlanByTriple);
    if (adapted) {
      scored.push(adapted);
      scoredKeys.add(key);
      rank += 1;
    }
  }

  // Bench = brain.ranked (full eligible pool, sorted by cost) minus
  // the plans that made Top 4. Each entry carries score.gate*Passed
  // via adaptScored.gate_results so the UI can label the elimination
  // reason ("Provider OON" / "Meds not covered" / "Missing dental").
  const bench: ScoredPlan[] = [];
  let benchRank = scored.length + 1;
  for (const sp of brain.ranked) {
    const key = `${sp.row.contract_id}-${sp.row.plan_id}-${sp.row.segment_id}`;
    if (scoredKeys.has(key)) continue;
    const adapted = adaptScored(sp, benchRank, agentPlanByTriple);
    if (adapted) {
      bench.push(adapted);
      benchRank += 1;
    }
  }

  // Pull weights from the archetype rule (the brain doesn't return its
  // resolved weights on BrainOutput today). When user passes a
  // weightsOverride, runPlanBrain uses that — fall through to it via
  // the args supplied by the hook caller (passed in separately below).
  const archetypeRule = ARCHETYPE_RULES[brain.archetype] ?? ARCHETYPE_RULES.general;
  const weights: WeightProfile = archetypeRule.weights;

  // Utilization profile — pull from the first scored plan (utilization
  // is patient-level, identical across plans within a single run).
  const firstAU = brain.ranked[0]?.score.annualUtilization;
  const utilizationProfile: CompatUtilizationProfile = firstAU
    ? adaptUtilization(firstAU)
    : { pcp: 2, specialist: 0, labs: 1, erProbability: 0.05, hospitalProbability: 0.02, hospitalDays: 3, monthlySupplies: 0 };

  // Map utilization category from PCP visit count (rough heuristic;
  // matches the old buckets — healthy ~2, moderate ~4, high 5+).
  const utilization: 'low' | 'moderate' | 'high' =
    utilizationProfile.pcp >= 5
      ? 'high'
      : utilizationProfile.pcp >= 4
        ? 'moderate'
        : 'low';

  return {
    population: adaptPopulation(brain.population),
    weights,
    utilization,
    utilizationProfile,
    scored,
    bench,
    filteredOut: [],
    detectedConditions: adaptDetectedConditions(brain.detectedConditions),
    medicationPatterns: adaptMedicationPatterns(brain.medicationPatterns),
    archetype: adaptArchetype(brain.archetype),
    csnpNote: brain.csnpNote,
    unresolvedDrugs: brain.unresolvedDrugs ?? [],
  };
}

// ─── The hook ────────────────────────────────────────────────────────

export function usePlanBrain(args: Args): State {
  const {
    plans,
    client,
    medications,
    providers,
    conditionProfile,
    userPriorities,
    weightOverride,
  } = args;

  const planIds = useMemo(() => plans.map((p) => p.id).sort().join(','), [plans]);
  const rxcuis = useMemo(
    () => medications.map((m) => m.rxcui).filter((x): x is string => !!x).sort().join(','),
    [medications],
  );

  // ── NPPES resolution for providers entered without an NPI ─────────
  // History: any provider lacking an NPI (CapturePanel-extracted, manual
  // typing, hydration with missing field) was silently dropped by the
  // `npis` filter below — Gate 1 ran with `userHasProviders=false` and
  // returned the whole pool unchanged, letting OON plans win on cost
  // alone. We now resolve the missing NPI via NPPES before the brain
  // run; unresolved providers surface as `unresolvedProviderNames` so
  // the UI can warn the user that network status is unknown.
  const [resolvedNpiById, setResolvedNpiById] = useState<Record<string, string>>({});
  const [unresolvedProviderNames, setUnresolvedProviderNames] = useState<string[]>([]);

  // Stable key for the resolution effect: any provider lacking an
  // intrinsic NPI gets keyed by id+name+state so re-renders don't
  // refire NPPES unless the unresolved set itself changes.
  const missingNpiKey = useMemo(() => {
    const missing = providers.filter((p) => !p.npi);
    return missing.map((p) => `${p.id}|${p.name}`).sort().join(',') + `|${client.state ?? ''}`;
  }, [providers, client.state]);

  useEffect(() => {
    const missing = providers.filter(
      (p) => !p.npi && !resolvedNpiById[p.id],
    );
    if (missing.length === 0) {
      // Recompute the unresolved-names list against current providers.
      const stillUnresolved = providers
        .filter((p) => !p.npi && !resolvedNpiById[p.id])
        .map((p) => p.name);
      setUnresolvedProviderNames((prev) =>
        prev.length === stillUnresolved.length &&
        prev.every((n, i) => n === stillUnresolved[i])
          ? prev
          : stillUnresolved,
      );
      return;
    }
    const controller = new AbortController();
    (async () => {
      const newlyResolved: Record<string, string> = {};
      const stillUnresolved: string[] = [];
      for (const p of missing) {
        try {
          const qs = new URLSearchParams({ name: p.name, limit: '1' });
          if (client.state) qs.set('state', client.state);
          const r = await fetch(`/api/npi-search?${qs.toString()}`, {
            signal: controller.signal,
          });
          if (!r.ok) {
            stillUnresolved.push(p.name);
            continue;
          }
          const body = (await r.json()) as { results?: Array<{ number?: string | number }> };
          const first = body.results?.[0]?.number;
          if (first != null) {
            newlyResolved[p.id] = String(first);
          } else {
            stillUnresolved.push(p.name);
          }
        } catch (err) {
          if ((err as { name?: string })?.name === 'AbortError') return;
          stillUnresolved.push(p.name);
        }
      }
      if (controller.signal.aborted) return;
      if (Object.keys(newlyResolved).length > 0) {
        setResolvedNpiById((prev) => ({ ...prev, ...newlyResolved }));
      }
      setUnresolvedProviderNames(stillUnresolved);
    })();
    return () => controller.abort();
    // missingNpiKey changes when the set of unresolved providers
    // changes; that's the only thing that should refire NPPES.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingNpiKey]);

  // Effective NPI list: union of intrinsic + NPPES-resolved.
  const npis = useMemo(
    () =>
      providers
        .map((p) => p.npi ?? resolvedNpiById[p.id])
        .filter((x): x is string => !!x)
        .sort()
        .join(','),
    [providers, resolvedNpiById],
  );

  const [data, setData] = useState<PlanBrainData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!planIds) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    void (async () => {
      // ── FHIR pre-warm via library (best-effort, non-fatal) ─────────
      // Single source of truth: same /api/library/provider-network
      // endpoint that ProvidersScreen calls via checkNetworkBatch. Hits
      // pm_provider_network_cache, falls back to FHIR live for UHC /
      // Humana / BCBS NC / Devoted on cache misses, upserts resolved
      // rows. The downstream /api/plan-brain-data read picks up the
      // freshly-populated rows.
      //
      // Previously this hit a local agent copy of
      // /api/provider-network-status. The library call replaced it so
      // there's exactly one pipeline writing to the cache from the
      // agent; the local copy (year-less) was removed Oct 2026.
      //
      // Failures here are intentionally swallowed. The brain still
      // runs against whatever's in cache; Gate 1 passes Unknown (see
      // plan-brain.ts:applyProviderGate) so the user always sees
      // plans, just with the unverified badge.
      const npiList = npis ? npis.split(',').filter(Boolean) : [];
      if (npiList.length > 0 && client.state && client.county) {
        try {
          const warmYear = planYearOverride();
          await checkProviderNetwork({
            npis: npiList,
            state: client.state,
            county: client.county,
            plan_ids: planIds.split(',').filter(Boolean),
            ...(warmYear !== null ? { plan_year: warmYear } : {}),
            signal: controller.signal,
          });
        } catch (err) {
          if ((err as { name?: string })?.name === 'AbortError') return;
          console.warn(
            '[plan-brain] library provider-network pre-warm failed (non-fatal):',
            err,
          );
        }
      }
      if (controller.signal.aborted) return;

      // ── Cache read ─────────────────────────────────────────────────
      try {
        const qs = new URLSearchParams({ ids: planIds });
        if (rxcuis) qs.set('rxcuis', rxcuis);
        if (npis) qs.set('npis', npis);
        // Same catalog year the plan rows and the Part D math use.
        const yearOverride = planYearOverride();
        if (yearOverride !== null) qs.set('plan_year', String(yearOverride));
        const res = await fetch(`/api/plan-brain-data?${qs.toString()}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`plan-brain-data ${res.status}`);
        const d = (await res.json()) as PlanBrainData;
        if (!controller.signal.aborted) setData(d);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // No silent degradation — empty `networkByPlan` would let every
        // OON plan compete on cost alone (Mode B in the brain-funnel
        // diagnostic). Surface the error and clear `data` so consumers
        // render a banner instead of a misleading ranking.
        setError((err as Error).message);
        setData(null);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
    // client.state + .county are in the deps so the brain re-runs once
    // AgentBase hydration finishes (it often lands them a tick after
    // mount). Without the deps, the initial fire skips the library
    // pre-warm and the brain reads a non-FHIR cache for the rest of
    // the session — same hydration race the ProvidersScreen fix in
    // 00b984f closed for its own effect.
  }, [planIds, rxcuis, npis, client.state, client.county]);

  // ── Live Medicare.gov gross prices for the client's drugs ─────────
  // pm_drug_cost_cache only covers ~73 NDCs; without this every other drug
  // was priced at a flat per-tier notional. One live lookup per drug set
  // (cached 24h server-side) gives the brain real prices; it re-runs when
  // they land. Failure leaves the old fallback in place.
  const grossKey = useMemo(
    () =>
      medications
        .filter((m) => m.rxcui)
        .map((m) => `${m.rxcui}:${monthlyQuantity(m)}`)
        .sort()
        .join(','),
    [medications],
  );
  const samplePlanKey = useMemo(() => {
    const seen = new Set<string>();
    const picks: string[] = [];
    for (const p of plans) {
      if (!p.has_drug_coverage || seen.has(p.contract_id)) continue;
      seen.add(p.contract_id);
      picks.push(p.id);
      if (picks.length >= 3) break;
    }
    return picks.join(',');
  }, [plans]);
  const [grossMonthlyByRxcui, setGrossMonthlyByRxcui] = useState<Map<string, number> | null>(null);
  useEffect(() => {
    setGrossMonthlyByRxcui(null);
    if (!grossKey || !samplePlanKey) return;
    const controller = new AbortController();
    const samples = samplePlanKey.split(',').map((id) => {
      const [contract_id, plan_number, seg] = id.split('-');
      return { contract_id, plan_number, segment_id: String(Number(seg ?? '0')) };
    });
    fetchGrossMonthlyPrices(
      medications
        .filter((m): m is Medication & { rxcui: string } => !!m.rxcui)
        .map((m) => ({ rxcui: m.rxcui, name: m.name, form: m.form, quantity: m.quantity, frequency: m.frequency, refillDays: m.refillDays })),
      samples,
      controller.signal,
    )
      .then((m) => {
        if (controller.signal.aborted) return;
        console.info(`[plan-brain] live gross prices for ${m.size} drug(s)`);
        setGrossMonthlyByRxcui(m);
      })
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        console.warn('[plan-brain] live gross prices failed (falling back):', (err as Error).message);
      });
    return () => controller.abort();
    // grossKey/samplePlanKey capture the drug set, quantities and plans.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossKey, samplePlanKey]);

  const ready = !loading && data !== null && plans.length > 0;

  // Build a lookup so adaptScored can resolve PmPlanRow → original Plan.
  const agentPlanByTriple = useMemo(() => {
    const m = new Map<string, Plan>();
    for (const p of plans) m.set(p.id, p);
    return m;
  }, [plans]);

  const result = useMemo<PlanBrainResult | null>(() => {
    if (!ready || !data) return null;
    // Build BrainInputs.
    const weightsOverride: BrainWeights | null =
      weightOverride && weightOverride.drug != null && weightOverride.oop != null && weightOverride.extras != null
        ? {
            drug: weightOverride.drug,
            oop: weightOverride.oop,
            extras: weightOverride.extras,
          }
        : null;
    // Substitute NPPES-resolved NPIs into providers without one so the
    // brain's Gate 1 actually runs against a populated NPI list.
    const effectiveProviders: Provider[] = providers.map((p) =>
      p.npi || !resolvedNpiById[p.id] ? p : { ...p, npi: resolvedNpiById[p.id] },
    );
    const brainInputs = adaptToBrainInputs({
      plans,
      client,
      medications,
      providers: effectiveProviders,
      data,
      conditionProfile,
      userPriorities,
      weightsOverride,
      grossMonthlyByRxcui: grossMonthlyByRxcui ?? undefined,
    });
    const out = runPlanBrain(brainInputs);
    const compat = adaptBrainOutput(out, agentPlanByTriple);
    // When the user supplied a weightsOverride, the brain ran with
    // those weights — surface them on the compat result so the UI's
    // weight readout reflects what actually scored.
    if (weightsOverride) compat.weights = weightsOverride;
    return compat;
  }, [
    ready,
    data,
    plans,
    client,
    medications,
    providers,
    resolvedNpiById,
    conditionProfile,
    userPriorities,
    weightOverride,
    agentPlanByTriple,
    grossMonthlyByRxcui,
  ]);

  return { result, data, loading, error, ready, unresolvedProviderNames, grossMonthlyByRxcui };
}
